/**
 * MemberDetailView
 * Level 2 drill-in: shows the active member's outcome step + unsatisfied preconditions.
 * Satisfied preconditions are HIDDEN; unsatisfied ones are ELEVATED with their CTA.
 * The outcome action is DISABLED until all preconditions are satisfied (BR-JO-08).
 */

import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { PreconditionCard, StepCard } from './PreconditionCard';
import { WorkflowActionButton } from './WorkflowActionButton';
import type { MemberWorkflowSummary } from '../../types/workflow';

const MEMBER_TYPE_LABELS: Record<string, string> = {
  lease_setup: 'Lease Setup',
  payment_setup: 'Payment Setup',
  move_in_walkthrough: 'Move-In Walkthrough',
  move_out_walkthrough: 'Move-Out Walkthrough',
};

interface MemberDetailViewProps {
  member: MemberWorkflowSummary;
  onBack: () => void;
  /** Accessible label for the back control — differs for saga members vs standalone atomics. */
  backLabel?: string;
}

export function MemberDetailView({ member, onBack, backLabel = 'Back to member chain' }: MemberDetailViewProps) {
  const unsatisfiedPreconditions = member.preconditions.filter((p) => !p.satisfied);
  const allSatisfied = unsatisfiedPreconditions.length === 0;
  const label = MEMBER_TYPE_LABELS[member.type] ?? member.type;

  // Split items by presentation kind, preserving each item's ORIGINAL index so CTA testids
  // (workflow-precondition-cta-{index}) stay stable for deep-links.
  // - Prerequisites: one-time gates (e.g. bank linking) — HIDDEN once satisfied (BR-JO-08).
  // - Steps: per-instance config — stay VISIBLE with a ✓/○ completion indicator.
  const indexed = member.preconditions.map((p, i) => ({ p, i }));
  const prerequisites = indexed.filter(({ p }) => (p.kind ?? 'prerequisite') === 'prerequisite');
  const steps = indexed.filter(({ p }) => p.kind === 'step');
  const unsatisfiedPrereqs = prerequisites.filter(({ p }) => !p.satisfied);
  const stepsDone = steps.filter(({ p }) => p.satisfied).length;

  return (
    <div data-testid="workflow-member-detail" className="flex flex-col gap-4 p-4">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="self-start -ml-2"
        data-testid="workflow-member-detail-back-btn"
        aria-label={backLabel}
      >
        <ArrowLeft className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
        Back
      </Button>

      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        {member.currentStep !== null && (
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="workflow-member-step">
            {member.stepLabel ? `${member.stepLabel} · ` : ''}
            Step {member.currentStep} of {member.totalSteps}
          </p>
        )}
      </div>

      {/* Prerequisites — one-time gates, ELEVATED while unsatisfied, HIDDEN once satisfied. */}
      {unsatisfiedPrereqs.length > 0 && (
        <div className="flex flex-col gap-2" role="list" aria-label="Required before proceeding">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Required first
          </p>
          {prerequisites.map(({ p, i }) => (
            <PreconditionCard key={i} precondition={p} index={i} />
          ))}
        </div>
      )}

      {/* Steps — per-instance config, always visible with a ✓/○ completion indicator. */}
      {steps.length > 0 && (
        <div className="flex flex-col gap-2" role="list" aria-label="Setup steps">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Steps · {stepsDone} of {steps.length} done
          </p>
          {steps.map(({ p, i }, idx) => (
            <StepCard key={i} precondition={p} index={i} stepNumber={idx + 1} />
          ))}
        </div>
      )}

      {/* Outcome action — disabled until all preconditions satisfied */}
      {member.nextAction && (
        <div className="border-t border-border pt-4">
          <p className="text-xs text-muted-foreground mb-3">
            {allSatisfied
              ? 'Ready — take the next step:'
              : 'Resolve the items above before continuing:'}
          </p>
          <WorkflowActionButton
            action={member.nextAction}
            disabled={!allSatisfied}
            testId="workflow-outcome-action-btn"
          />
        </div>
      )}

      {member.status === 'completed' && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg">
          <span className="text-sm font-medium text-success">This step is complete.</span>
        </div>
      )}

      {member.status === 'upcoming' && (
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            This step will become available after the current step completes.
          </p>
        </div>
      )}
    </div>
  );
}
